import { useEffect } from 'react';

import * as Sentry from '@sentry/react';

import type { AccountSetupQuery } from '@spotify/confidence-graphql';

export type AccountSetupStatus = 'loaded' | 'loading' | 'error' | 'unavailable';

type AccountServiceTier = Extract<
  NonNullable<AccountSetupQuery['currentAccount']>,
  { __typename: 'AdminV1AccountInfo' }
>['serviceTier'];

export const useSentryBillingContext = ({
  accountSetupStatus,
  serviceTier,
}: {
  accountSetupStatus: AccountSetupStatus;
  serviceTier?: AccountServiceTier;
}): void => {
  const tier = serviceTier?.tier ?? null;
  const entitlements =
    serviceTier?.entitlements.map(({ key, value }) => ({
      key,
      booleanValue: value?.booleanValue ?? null,
      integerValue: value?.integerValue ?? null,
      unlimited: Boolean(value?.unlimited),
    })) ?? null;
  const serializedEntitlements: string = JSON.stringify(entitlements);

  useEffect(() => {
    Sentry.setContext('billing', {
      accountSetupStatus,
      tier,
      entitlements: JSON.parse(serializedEntitlements) as typeof entitlements,
    });

    return () => Sentry.setContext('billing', null);
  }, [accountSetupStatus, tier, serializedEntitlements]);
};
